/**
 * Global SendBird Duck
 *
 * Manages global SendBird connection and unread message counts
 * that persist across all pages (not just conversation pages).
 *
 * This enables showing unread badges in the dashboard navigation
 * regardless of which page the user is on.
 */
import {
  initializeSendbird,
  disconnectSendbird,
  createChannelCollection,
  disposeChannelCollection,
  getConnectionStatus,
  CHANNEL_TYPES,
} from '../util/sendbird';
import { sendbirdAPI } from '../util/api';

// ================ Action types ================ //

export const GLOBAL_SENDBIRD_INIT_REQUEST = 'app/sendbird/GLOBAL_SENDBIRD_INIT_REQUEST';
export const GLOBAL_SENDBIRD_INIT_SUCCESS = 'app/sendbird/GLOBAL_SENDBIRD_INIT_SUCCESS';
export const GLOBAL_SENDBIRD_INIT_ERROR = 'app/sendbird/GLOBAL_SENDBIRD_INIT_ERROR';

export const GLOBAL_SENDBIRD_DISCONNECT = 'app/sendbird/GLOBAL_SENDBIRD_DISCONNECT';

export const SET_TOTAL_UNREAD_COUNT = 'app/sendbird/SET_TOTAL_UNREAD_COUNT';
export const SET_CREATOR_UNREAD_COUNT = 'app/sendbird/SET_CREATOR_UNREAD_COUNT';
export const SET_ORG_UNREAD_COUNT = 'app/sendbird/SET_ORG_UNREAD_COUNT';
export const UPDATE_UNREAD_COUNTS = 'app/sendbird/UPDATE_UNREAD_COUNTS';

// ================ Reducer ================ //

// Minimum ms between initialization attempts (30 seconds)
const SENDBIRD_RETRY_COOLDOWN = 30000;

const initialState = {
  // Connection state
  sendbirdInitialized: false,
  sendbirdInitializing: false,
  sendbirdInitError: null,
  sendbirdToken: null,
  sendbirdLastAttemptAt: null,

  // Unread counts
  totalUnreadCount: 0,
  creatorUnreadCount: 0,
  orgUnreadCounts: {}, // { [orgId]: count }
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case GLOBAL_SENDBIRD_INIT_REQUEST:
      return {
        ...state,
        sendbirdInitializing: true,
        sendbirdInitError: null,
        sendbirdLastAttemptAt: Date.now(),
      };

    case GLOBAL_SENDBIRD_INIT_SUCCESS:
      return {
        ...state,
        sendbirdInitializing: false,
        sendbirdInitialized: true,
        sendbirdToken: payload.token,
      };

    case GLOBAL_SENDBIRD_INIT_ERROR:
      return {
        ...state,
        sendbirdInitializing: false,
        sendbirdInitError: payload,
      };

    case GLOBAL_SENDBIRD_DISCONNECT:
      return {
        ...initialState,
      };

    case SET_TOTAL_UNREAD_COUNT:
      return {
        ...state,
        totalUnreadCount: payload,
      };

    case SET_CREATOR_UNREAD_COUNT:
      return {
        ...state,
        creatorUnreadCount: payload,
      };

    case SET_ORG_UNREAD_COUNT:
      return {
        ...state,
        orgUnreadCounts: {
          ...state.orgUnreadCounts,
          [payload.orgId]: payload.count,
        },
      };

    case UPDATE_UNREAD_COUNTS:
      return {
        ...state,
        totalUnreadCount: payload.totalUnreadCount,
        creatorUnreadCount: payload.creatorUnreadCount,
        orgUnreadCounts: payload.orgUnreadCounts,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const globalSendbirdInitRequest = () => ({
  type: GLOBAL_SENDBIRD_INIT_REQUEST,
});

export const globalSendbirdInitSuccess = token => ({
  type: GLOBAL_SENDBIRD_INIT_SUCCESS,
  payload: { token },
});

export const globalSendbirdInitError = error => ({
  type: GLOBAL_SENDBIRD_INIT_ERROR,
  payload: error,
});

export const globalSendbirdDisconnect = () => ({
  type: GLOBAL_SENDBIRD_DISCONNECT,
});

export const setTotalUnreadCount = count => ({
  type: SET_TOTAL_UNREAD_COUNT,
  payload: count,
});

export const setCreatorUnreadCount = count => ({
  type: SET_CREATOR_UNREAD_COUNT,
  payload: count,
});

export const setOrgUnreadCount = (orgId, count) => ({
  type: SET_ORG_UNREAD_COUNT,
  payload: { orgId, count },
});

export const updateUnreadCounts = counts => ({
  type: UPDATE_UNREAD_COUNTS,
  payload: counts,
});

// ================ Selectors ================ //

export const selectSendbirdInitialized = state => state.sendbird.sendbirdInitialized;
export const selectSendbirdInitializing = state => state.sendbird.sendbirdInitializing;
export const selectSendbirdInitError = state => state.sendbird.sendbirdInitError;

export const selectTotalUnreadCount = state => state.sendbird.totalUnreadCount;
export const selectCreatorUnreadCount = state => state.sendbird.creatorUnreadCount;
export const selectOrgUnreadCounts = state => state.sendbird.orgUnreadCounts;
export const selectOrgUnreadCount = (state, orgId) => state.sendbird.orgUnreadCounts[orgId] || 0;

// ================ Helper functions ================ //

/**
 * Calculate unread counts from channels, separating by context (creator vs org)
 * Channels are categorized based on their data.orgId field:
 * - Channels with orgId belong to that organization
 * - Channels without orgId belong to creator context
 */
const calculateUnreadCounts = channels => {
  let totalUnreadCount = 0;
  let creatorUnreadCount = 0;
  const orgUnreadCounts = {};

  channels.forEach(channel => {
    const unreadCount = channel.unreadMessageCount || 0;
    totalUnreadCount += unreadCount;

    // Check if channel belongs to an organization
    const orgId = channel.data?.orgId;

    if (orgId) {
      // Organization channel
      orgUnreadCounts[orgId] = (orgUnreadCounts[orgId] || 0) + unreadCount;
    } else {
      // Creator channel (no orgId)
      creatorUnreadCount += unreadCount;
    }
  });

  return {
    totalUnreadCount,
    creatorUnreadCount,
    orgUnreadCounts,
  };
};

// Keep reference to global collection for cleanup
let globalChannelCollection = null;

// ================ Thunks ================ //

// Badges are refreshed at most this often as members move around the site.
const UNREAD_COUNTS_MIN_INTERVAL = 60 * 1000;
let unreadCountsFetchedAt = 0;

/**
 * Unread counts for the badges, from our server (/api/sendbird/unread-counts).
 *
 * This replaces opening a Sendbird connection on every page for every
 * signed-in visitor (initializeGlobalSendbird), which made each of them a
 * billed Sendbird monthly user. The connection now opens only where chat is
 * used: the Conversations pages, and Insights for poll results.
 */
export const fetchUnreadCounts = ({ force = false } = {}) => async (dispatch, getState) => {
  if (!getState().user?.currentUser?.id?.uuid) return null;
  if (!force && Date.now() - unreadCountsFetchedAt < UNREAD_COUNTS_MIN_INTERVAL) return null;
  unreadCountsFetchedAt = Date.now();
  try {
    const response = await sendbirdAPI.getUnreadCounts();
    const body = response?.data ?? response;
    const counts = body?.data ?? body;
    if (counts && typeof counts.totalUnreadCount === 'number') {
      dispatch(updateUnreadCounts(counts));
    }
    return counts;
  } catch (e) {
    return null;
  }
};

/**
 * Initialize global SendBird connection after login
 * Creates a lightweight channel collection just for tracking unread counts
 */
export const initializeGlobalSendbird = () => async (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;

  // Don't initialize if already initializing or successfully initialized
  if (state.sendbird.sendbirdInitializing || state.sendbird.sendbirdInitialized) {
    return;
  }

  if (!currentUser?.id?.uuid) {
    return;
  }

  // Rate limit: don't retry within cooldown period after a failed attempt
  const lastAttempt = state.sendbird.sendbirdLastAttemptAt;
  if (lastAttempt && state.sendbird.sendbirdInitError) {
    const elapsed = Date.now() - lastAttempt;
    if (elapsed < SENDBIRD_RETRY_COOLDOWN) {
      return;
    }
  }

  dispatch(globalSendbirdInitRequest());

  try {
    // Get SendBird token from server
    const tokenResponse = await sendbirdAPI.getToken();
    const { userId, accessToken: token } = tokenResponse;

    // Initialize SendBird SDK
    await initializeSendbird(userId, token);

    dispatch(globalSendbirdInitSuccess(token));

    // Create channel collection for unread tracking
    // This is lightweight - just tracks channel updates for unread counts
    const { channels, collection } = await createChannelCollection(
      {
        limit: 100, // Load enough channels to get accurate unread counts
      },
      {
        // Real-time handlers for channel updates
        onChannelsAdded: addedChannels => {
          dispatch(recalculateUnreadCountsFromChannels([
            ...addedChannels,
            ...(getState().sendbird._cachedChannels || []),
          ]));
        },
        onChannelsUpdated: updatedChannels => {
          // Get current channels and merge with updates
          const currentChannels = getState().sendbird._cachedChannels || [];
          const mergedChannels = currentChannels.map(c => {
            const updated = updatedChannels.find(u => u.channelUrl === c.channelUrl);
            return updated || c;
          });
          // Add any new channels from updates
          updatedChannels.forEach(updated => {
            if (!mergedChannels.find(c => c.channelUrl === updated.channelUrl)) {
              mergedChannels.push(updated);
            }
          });
          dispatch(recalculateUnreadCountsFromChannels(mergedChannels));
        },
        onChannelsDeleted: deletedUrls => {
          const currentChannels = getState().sendbird._cachedChannels || [];
          const remainingChannels = currentChannels.filter(
            c => !deletedUrls.includes(c.channelUrl)
          );
          dispatch(recalculateUnreadCountsFromChannels(remainingChannels));
        },
      }
    );

    globalChannelCollection = collection;

    // Calculate and set initial unread counts
    dispatch(recalculateUnreadCountsFromChannels(channels));

  } catch (error) {
    console.error('Failed to initialize global SendBird:', error);
    dispatch(globalSendbirdInitError(error.message || 'Failed to initialize SendBird'));
  }
};

/**
 * Recalculate unread counts from a list of channels
 * Also caches the channels for future recalculations
 */
const recalculateUnreadCountsFromChannels = channels => (dispatch, getState) => {
  // Calculate counts
  const counts = calculateUnreadCounts(channels);

  // Update state
  dispatch(updateUnreadCounts(counts));

  // Cache channels for future reference (stored in closure, not state)
  // This is a workaround since we don't want to store full channel objects in Redux
  const state = getState();
  state.sendbird._cachedChannels = channels;
};

/**
 * Disconnect global SendBird connection on logout
 */
export const disconnectGlobalSendbird = () => async dispatch => {
  try {
    // Dispose channel collection
    if (globalChannelCollection) {
      disposeChannelCollection();
      globalChannelCollection = null;
    }

    // Disconnect SendBird
    await disconnectSendbird();

    dispatch(globalSendbirdDisconnect());
  } catch (error) {
    console.error('Error disconnecting global SendBird:', error);
    // Still dispatch disconnect to clear state
    dispatch(globalSendbirdDisconnect());
  }
};

/**
 * Manually refresh unread counts
 * Call this when you know counts have changed (e.g., after marking as read)
 */
export const refreshUnreadCounts = () => async (dispatch, getState) => {
  const state = getState();
  const cachedChannels = state.sendbird._cachedChannels || [];

  if (cachedChannels.length > 0) {
    dispatch(recalculateUnreadCountsFromChannels(cachedChannels));
  }
};

/**
 * Update a single channel's unread count
 * Call this from conversation pages when a channel is marked as read
 */
export const updateChannelUnreadCount = (channelUrl, newUnreadCount) => (dispatch, getState) => {
  const state = getState();
  const cachedChannels = state.sendbird._cachedChannels || [];

  const updatedChannels = cachedChannels.map(channel => {
    if (channel.channelUrl === channelUrl) {
      return { ...channel, unreadMessageCount: newUnreadCount };
    }
    return channel;
  });

  dispatch(recalculateUnreadCountsFromChannels(updatedChannels));
};
